import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

// No rehype-raw: raw HTML in the source is escaped, so no sanitizer is needed.
// remark-breaks keeps single "\n" breaks from descriptions written as plain text.
const REMARK_PLUGINS = [remarkGfm, remarkBreaks];

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const OPENS_EXTERNALLY = /^https?:/i;
const EMAIL = /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/;
const HOST_WITH_DOT = /^[^\s@/]+\.[^\s@/]+/;

// uriTransformer returns allowed URIs unchanged and replaces the rest with a
// script URL; an empty href renders the link text as plain text instead.
// Scheme-less addresses would resolve against the careers page, so they are
// given a scheme.
const transformLinkUri = (uri) => {
  const url = (uri || '').trim();
  const safe = ReactMarkdown.uriTransformer(url);
  if (safe !== url) return '';
  if (
    safe === '' ||
    HAS_SCHEME.test(safe) ||
    safe.startsWith('/') ||
    safe.startsWith('#') ||
    safe.startsWith('?')
  ) {
    return safe;
  }
  if (EMAIL.test(safe)) return `mailto:${safe}`;
  if (HOST_WITH_DOT.test(safe)) return `https://${safe}`;
  return safe;
};

// The position name is an h3, so description headings start at h4.
const COMPONENTS = {
  h1: ({ children }) => <h4>{children}</h4>,
  h2: ({ children }) => <h5>{children}</h5>,
  h3: ({ children }) => <h6>{children}</h6>,
  h4: ({ children }) => <h6>{children}</h6>,
  h5: ({ children }) => <h6>{children}</h6>,
  h6: ({ children }) => <h6>{children}</h6>,
  a: ({ href, children }) => {
    if (!href) return <>{children}</>;
    if (!OPENS_EXTERNALLY.test(href)) return <a href={href}>{children}</a>;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
        <span className="__job-description-sr-only"> (opens in new tab)</span>
      </a>
    );
  },
  img: () => null,
  pre: ({ children }) => (
    <pre
      className="__job-description-scroll"
      tabIndex={0}
      role="region"
      aria-label="Code sample"
    >
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div
      className="__job-description-scroll"
      tabIndex={0}
      role="region"
      aria-label="Table"
    >
      <table>{children}</table>
    </div>
  ),
};

const JobDescription = ({ content }) => (
  <ReactMarkdown
    className="__job-description"
    remarkPlugins={REMARK_PLUGINS}
    transformLinkUri={transformLinkUri}
    components={COMPONENTS}
  >
    {content}
  </ReactMarkdown>
);

export default JobDescription;
